import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  Paper,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Avatar,
} from '@mui/material';
import {
  AlertCircle,
  Plus,
  Trash2,
  ExternalLink,
  Bug,
  Lightbulb,
  Zap,
  CheckSquare,
  HelpCircle,
  GitBranch,
  ArrowRight,
  CheckCircle,
  Clock,
  XCircle,
} from 'lucide-react';
import dayjs from 'dayjs';
import { Task, LinkedIssue, IssueProvider, IssueStatus, IssuePriority, IssueType } from '@/types';

interface TaskIssuesTabProps {
  task: Task;
  allTasks: Task[];
  onAddIssue?: (issue: Omit<LinkedIssue, 'id' | 'linkedAt' | 'linkedBy'>) => Promise<void>;
  onRemoveIssue?: (issueId: string) => Promise<void>;
  onUpdateIssueStatus?: (issueId: string, status: IssueStatus) => Promise<void>;
  onCreateIssueSubtask?: (issue: Omit<LinkedIssue, 'id' | 'linkedAt' | 'linkedBy'>) => Promise<void>;
  isLoading?: boolean;
  canEdit?: boolean;
}

interface AddIssueModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (issue: Omit<LinkedIssue, 'id' | 'linkedAt' | 'linkedBy'>) => Promise<void>;
  onCreateSubtask: (issue: Omit<LinkedIssue, 'id' | 'linkedAt' | 'linkedBy'>) => Promise<void>;
  parentTaskId: string;
}

// Issue type icons
const getIssueTypeIcon = (type: IssueType) => {
  switch (type) {
    case 'bug':
      return <Bug size={16} style={{ color: '#ef4444' }} />;
    case 'feature':
      return <Lightbulb size={16} style={{ color: '#8b5cf6' }} />;
    case 'improvement':
      return <Zap size={16} style={{ color: '#f59e0b' }} />;
    case 'task':
      return <CheckSquare size={16} style={{ color: '#3b82f6' }} />;
    case 'question':
      return <HelpCircle size={16} style={{ color: '#6b7280' }} />;
    default:
      return <AlertCircle size={16} style={{ color: '#64748b' }} />;
  }
};

// Issue status icons
const getIssueStatusInfo = (status: IssueStatus) => {
  switch (status) {
    case 'open':
      return { icon: <AlertCircle size={14} />, label: 'Open', color: '#3b82f6', bgColor: '#dbeafe' };
    case 'in_progress':
      return { icon: <Clock size={14} />, label: 'In Progress', color: '#f59e0b', bgColor: '#fef3c7' };
    case 'resolved':
      return { icon: <CheckCircle size={14} />, label: 'Resolved', color: '#10b981', bgColor: '#d1fae5' };
    case 'closed':
      return { icon: <XCircle size={14} />, label: 'Closed', color: '#6b7280', bgColor: '#f3f4f6' };
    default:
      return { icon: <AlertCircle size={14} />, label: status, color: '#64748b', bgColor: '#f1f5f9' };
  }
};

// Priority colors
const getPriorityColor = (priority: IssuePriority) => {
  switch (priority) {
    case 'critical':
      return { color: '#dc2626', bgColor: '#fee2e2' };
    case 'high':
      return { color: '#ea580c', bgColor: '#ffedd5' };
    case 'medium':
      return { color: '#ca8a04', bgColor: '#fef9c3' };
    case 'low':
      return { color: '#16a34a', bgColor: '#dcfce7' };
    default:
      return { color: '#64748b', bgColor: '#f1f5f9' };
  }
};

// Provider info
const getProviderInfo = (provider: IssueProvider) => {
  switch (provider) {
    case 'github':
      return { label: 'GitHub', color: '#24292e', bgColor: '#f6f8fa' };
    case 'gitlab':
      return { label: 'GitLab', color: '#fc6d26', bgColor: '#fff4f0' };
    case 'jira':
      return { label: 'Jira', color: '#0052cc', bgColor: '#deebff' };
    case 'internal':
    default:
      return { label: 'Internal', color: '#6366f1', bgColor: '#eef2ff' };
  }
};

// Add Issue Modal
const AddIssueModal: React.FC<AddIssueModalProps> = ({ 
  open, 
  onClose, 
  onAdd, 
  onCreateSubtask,
  parentTaskId 
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createAsSubtask, setCreateAsSubtask] = useState(true);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    provider: 'internal' as IssueProvider,
    type: 'bug' as IssueType,
    priority: 'medium' as IssuePriority,
    externalUrl: '',
    externalId: '',
    labels: [] as string[],
  });

  const handleSubmit = async () => {
    if (!formData.title.trim()) return;
    
    setIsSubmitting(true);
    try {
      const issueData: Omit<LinkedIssue, 'id' | 'linkedAt' | 'linkedBy'> = {
        title: formData.title.trim(),
        description: formData.description.trim() || undefined,
        provider: formData.provider,
        type: formData.type,
        priority: formData.priority,
        status: 'open',
        labels: formData.labels,
        assigneeIds: [],
        externalUrl: formData.externalUrl.trim() || undefined,
        externalId: formData.externalId.trim() || undefined,
      };

      if (createAsSubtask) {
        await onCreateSubtask(issueData);
      } else {
        await onAdd(issueData);
      }
      
      setFormData({
        title: '',
        description: '',
        provider: 'internal',
        type: 'bug',
        priority: 'medium',
        externalUrl: '',
        externalId: '',
        labels: [],
      });
      onClose();
    } catch (error) {
      console.error('Failed to add issue:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AlertCircle size={20} />
          Create Issue
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Issue Title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="e.g., Fix login button not responding"
            fullWidth
            required
          />
          
          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Describe the issue in detail..."
            fullWidth
            multiline
            rows={3}
          />

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as IssueType })}
                label="Type"
              >
                <MenuItem value="bug">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Bug size={16} style={{ color: '#ef4444' }} /> Bug
                  </Box>
                </MenuItem>
                <MenuItem value="feature">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Lightbulb size={16} style={{ color: '#8b5cf6' }} /> Feature
                  </Box>
                </MenuItem>
                <MenuItem value="improvement">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Zap size={16} style={{ color: '#f59e0b' }} /> Improvement
                  </Box>
                </MenuItem>
                <MenuItem value="task">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckSquare size={16} style={{ color: '#3b82f6' }} /> Task
                  </Box>
                </MenuItem>
                <MenuItem value="question">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HelpCircle size={16} style={{ color: '#6b7280' }} /> Question
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value as IssuePriority })}
                label="Priority"
              >
                <MenuItem value="critical">Critical</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="low">Low</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <FormControl fullWidth>
            <InputLabel>Source</InputLabel>
            <Select
              value={formData.provider}
              onChange={(e) => setFormData({ ...formData, provider: e.target.value as IssueProvider })}
              label="Source"
            >
              <MenuItem value="internal">Internal (Create New)</MenuItem>
              <MenuItem value="github">GitHub (Link Existing)</MenuItem>
              <MenuItem value="gitlab">GitLab (Link Existing)</MenuItem>
              <MenuItem value="jira">Jira (Link Existing)</MenuItem>
            </Select>
          </FormControl>

          {formData.provider !== 'internal' && (
            <>
              <TextField
                label="External Issue ID"
                value={formData.externalId}
                onChange={(e) => setFormData({ ...formData, externalId: e.target.value })}
                placeholder={formData.provider === 'github' ? '#123' : formData.provider === 'jira' ? 'PROJ-123' : ''}
                fullWidth
                size="small"
              />
              <TextField
                label="External URL"
                value={formData.externalUrl}
                onChange={(e) => setFormData({ ...formData, externalUrl: e.target.value })}
                placeholder="https://github.com/..."
                fullWidth
                size="small"
              />
            </>
          )}

          <Autocomplete
            multiple
            freeSolo
            options={['bug', 'feature', 'enhancement', 'documentation', 'urgent', 'wontfix']}
            value={formData.labels}
            onChange={(_, newValue) => setFormData({ ...formData, labels: newValue })}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="Labels" placeholder="Add labels" size="small" />
            )}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={createAsSubtask}
                onChange={(e) => setCreateAsSubtask(e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body2">Create as subtask with "issue" tag</Typography>
                <Typography variant="caption" color="text.secondary">
                  The issue will be created as a dependent subtask with automatic blocking relationship
                </Typography>
              </Box>
            }
          />

          {createAsSubtask && (
            <Alert severity="info">
              <Typography variant="body2">
                This will create a subtask with:
                <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
                  <li>Tag: <strong>issue</strong>, <strong>{formData.type}</strong></li>
                  <li>Dependency: Will block parent task until resolved</li>
                  <li>Auto-linked back to this task</li>
                </ul>
              </Typography>
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!formData.title.trim() || isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : <Plus size={16} />}
        >
          {createAsSubtask ? 'Create Issue Subtask' : 'Link Issue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * TaskIssuesTab
 * Manage issues linked to a task, with ability to create issues as subtasks
 */
const TaskIssuesTab: React.FC<TaskIssuesTabProps> = ({
  task,
  allTasks,
  onAddIssue,
  onRemoveIssue,
  onUpdateIssueStatus,
  onCreateIssueSubtask,
  isLoading,
  canEdit,
}) => {
  const editable = canEdit ?? true;
  const [showAddModal, setShowAddModal] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const issues = task.linkedIssues || [];

  const handleRemoveIssue = async (issueId: string) => {
    if (!onRemoveIssue || !editable) return;
    setRemovingId(issueId);
    try {
      await onRemoveIssue(issueId);
    } finally {
      setRemovingId(null);
    }
  };

  // Get linked subtask for an issue
  const getLinkedSubtask = (issue: LinkedIssue) => {
    if (!issue.subtaskId) return null;
    return allTasks.find(t => t.id === issue.subtaskId);
  };

  // Count by status
  const statusCounts = issues.reduce((acc, issue) => {
    acc[issue.status] = (acc[issue.status] || 0) + 1;
    return acc;
  }, {} as Record<IssueStatus, number>);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Typography variant="subtitle2" color="text.secondary">
            {issues.length} issue{issues.length !== 1 ? 's' : ''}
          </Typography>
          {issues.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {statusCounts.open && (
                <Chip label={`${statusCounts.open} open`} size="small" sx={{ bgcolor: '#dbeafe', color: '#3b82f6' }} />
              )}
              {statusCounts.in_progress && (
                <Chip label={`${statusCounts.in_progress} in progress`} size="small" sx={{ bgcolor: '#fef3c7', color: '#f59e0b' }} />
              )}
              {statusCounts.resolved && (
                <Chip label={`${statusCounts.resolved} resolved`} size="small" sx={{ bgcolor: '#d1fae5', color: '#10b981' }} />
              )}
            </Box>
          )}
        </Box>
        {editable && onAddIssue && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<Plus size={16} />}
            onClick={() => setShowAddModal(true)}
          >
            Create Issue
          </Button>
        )}
      </Box>

      {/* GitHub Integration Status */}
      {task.integrations?.github?.enabled && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Connected to GitHub: {task.integrations.github.repositoryOwner}/{task.integrations.github.repositoryName}
          </Typography>
        </Alert>
      )}

      {/* Empty State */}
      {issues.length === 0 ? (
        <Paper
          sx={{
            py: 6,
            px: 3,
            textAlign: 'center',
            bgcolor: 'grey.50',
            border: '1px dashed',
            borderColor: 'grey.300',
          }}
        >
          <AlertCircle size={48} style={{ color: '#94a3b8', marginBottom: 16 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Issues Reported
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create issues to track bugs, feature requests, or improvements
          </Typography>
          {editable && onAddIssue && (
            <Button
              variant="contained"
              startIcon={<Plus size={16} />}
              onClick={() => setShowAddModal(true)}
            >
              Create First Issue
            </Button>
          )}
        </Paper>
      ) : (
        <List disablePadding>
          {issues.map((issue) => {
            const statusInfo = getIssueStatusInfo(issue.status);
            const priorityInfo = getPriorityColor(issue.priority);
            const providerInfo = getProviderInfo(issue.provider);
            const linkedSubtask = getLinkedSubtask(issue);
            const isRemoving = removingId === issue.id;

            return (
              <ListItem
                key={issue.id}
                sx={{
                  px: 2,
                  py: 1.5,
                  borderRadius: 1,
                  mb: 1,
                  bgcolor: issue.status === 'resolved' || issue.status === 'closed' ? 'grey.50' : 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  opacity: issue.status === 'closed' ? 0.7 : 1,
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {getIssueTypeIcon(issue.type)}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        sx={{
                          textDecoration: issue.status === 'closed' ? 'line-through' : 'none',
                        }}
                      >
                        {issue.title}
                      </Typography>
                      <Chip
                        icon={statusInfo.icon}
                        label={statusInfo.label}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.7rem',
                          bgcolor: statusInfo.bgColor,
                          color: statusInfo.color,
                          '& .MuiChip-icon': { color: statusInfo.color },
                        }}
                      />
                      <Chip
                        label={issue.priority}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          bgcolor: priorityInfo.bgColor,
                          color: priorityInfo.color,
                          textTransform: 'capitalize',
                        }}
                      />
                      {issue.provider !== 'internal' && (
                        <Chip
                          label={providerInfo.label}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            bgcolor: providerInfo.bgColor,
                            color: providerInfo.color,
                          }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 0.5 }}>
                      {issue.description && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          {issue.description.length > 100 
                            ? `${issue.description.substring(0, 100)}...` 
                            : issue.description}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="caption" color="text.secondary">
                          Created {dayjs(issue.linkedAt).fromNow()}
                        </Typography>
                        {linkedSubtask && (
                          <Chip
                            icon={<GitBranch size={12} />}
                            label={`Subtask: ${linkedSubtask.title}`}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                        )}
                        {issue.labels.map((label) => (
                          <Chip
                            key={label}
                            label={label}
                            size="small"
                            variant="outlined"
                            sx={{ height: 18, fontSize: '0.6rem' }}
                          />
                        ))}
                      </Box>
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  {issue.externalUrl && (
                    <Tooltip title="Open in external system">
                      <IconButton
                        size="small"
                        onClick={() => window.open(issue.externalUrl, '_blank')}
                        sx={{ mr: 0.5 }}
                      >
                        <ExternalLink size={16} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {editable && onUpdateIssueStatus && issue.status === 'open' && (
                    <Tooltip title="Mark as resolved">
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => onUpdateIssueStatus(issue.id, 'resolved')}
                        sx={{ mr: 0.5 }}
                      >
                        <CheckCircle size={16} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {editable && onRemoveIssue && (
                    <Tooltip title="Remove issue">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveIssue(issue.id)}
                        disabled={isRemoving}
                      >
                        {isRemoving ? <CircularProgress size={16} /> : <Trash2 size={16} />}
                      </IconButton>
                    </Tooltip>
                  )}
                </ListItemSecondaryAction>
              </ListItem>
            );
          })}
        </List>
      )}

      {/* Add Issue Modal */}
      {editable && onAddIssue && onCreateIssueSubtask && (
        <AddIssueModal
          open={showAddModal}
          onClose={() => setShowAddModal(false)}
          onAdd={onAddIssue}
          onCreateSubtask={onCreateIssueSubtask}
          parentTaskId={task.id}
        />
      )}
    </Box>
  );
};

export default TaskIssuesTab;
